import { BooLogo } from "@/components/BooLogo";
import { useMemo, type CSSProperties } from "react";
import { PreviewShell } from "./PreviewShell";

const CARDS = [
  { color: "#1D9E75", label: "Each Boo runs in its own secure sandbox" },
  { color: "#534AB7", label: "Helpful to anyone on your team, anytime" },
  { color: "#D4537E", label: "Connects to every tool your team works in" },
  { color: "#EF9F27", label: "Builds shared knowledge across your team" },
  { color: "#7F77DD", label: "Spin up specialized Boos for any role" },
] as const;

const TEAM_AVATARS = [
  { initials: "JL", bg: "#534AB7" },
  { initials: "SK", bg: "#1D9E75" },
  { initials: "AM", bg: "#D4537E" },
] as const;

function randomTilt() {
  // Pick a tilt in the range [-2.5°, 2.5°] but avoid near-zero values so every
  // card has a noticeable lean.
  const sign = Math.random() < 0.5 ? -1 : 1;
  const magnitude = 0.6 + Math.random() * 1.9;
  return `${(sign * magnitude).toFixed(2)}deg`;
}

export function Step1Preview() {
  const rotations = useMemo(() => CARDS.map(() => randomTilt()), []);

  return (
    <PreviewShell
      align="start"
      background="linear-gradient(135deg, #faf7fb 0%, #f0eef5 50%, #f5eef0 100%)"
      dotColors={["#D4537E", "#7F77DD", "#534AB7", "#EF9F27"]}
    >
      <div className="flex flex-col items-center gap-8">
        <div
          className="boo-fade-in relative flex items-center gap-3"
          style={{ animationDelay: "200ms" }}
        >
          <span
            className="boo-pulse absolute -inset-6 rounded-full"
            style={{
              background:
                "radial-gradient(closest-side, rgba(127, 119, 221, 0.4), transparent 70%)",
            }}
          />
          <BooLogo className="relative size-20 rounded-2xl" />
        </div>

        <div className="w-1/2 space-y-3">
          {CARDS.map((card, i) => (
            <div
              key={i}
              className="boo-spring-in flex items-start gap-3 rounded-xl border border-black/[0.06] bg-white px-5 py-7"
              style={{
                animationDelay: `${i * 90}ms`,
                boxShadow: "0 6px 16px rgba(20, 20, 30, 0.06)",
                ["--card-rot" as string]: rotations[i],
              } as CSSProperties}
            >
              <span
                className="mt-2 h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ background: card.color }}
              />
              <span className="text-[16px] font-normal text-stone-900 leading-snug">
                {card.label}
              </span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {TEAM_AVATARS.map((av, i) => (
              <span
                key={av.initials}
                className="boo-fade-in flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-[10px] font-medium text-white"
                style={{ background: av.bg, animationDelay: `${600 + i * 100}ms` }}
              >
                {av.initials}
              </span>
            ))}
          </div>
          <span className="text-[12px] text-stone-500">and your whole team</span>
        </div>
      </div>
    </PreviewShell>
  );
}
